import { Navigate, Outlet } from "react-router";

import { useAccount } from "../lib/account.ts";
import { BootScreen } from "../lib/Loading.tsx";
import { landingIntent } from "../login/intendedRole.ts";
import { resolveWorkspace } from "../workspace/workspace.model.ts";
import { PATHS, ROUTE_COPY } from "./routes.constants.ts";
import { workspacePath } from "./routes.model.ts";

/** Shown while the session lookup is in flight, before any route can decide. */
export function Booting() {
  return <BootScreen message={ROUTE_COPY.booting} />;
}

/**
 * Everything behind a session.
 *
 * The redirect is what makes a workspace URL safe to bookmark: an expired
 * session lands on sign-in rather than on a shell with no data. It is not a
 * permission check — the API decides what each request may see, and this only
 * decides which screen to draw.
 */
export function RequireSession() {
  const { session, loading } = useAccount();

  if (loading) return <Booting />;
  if (!session) return <Navigate to={PATHS.signIn} replace />;

  return <Outlet />;
}

/** Sign-in and apply. A signed-in caller has no business on either. */
export function SignedOutOnly() {
  const { session, loading } = useAccount();

  if (loading) return <Booting />;
  if (session) return <Navigate to={PATHS.root} replace />;

  return <Outlet />;
}

/**
 * `/` — the one address nobody has to know, and the only one that guesses.
 *
 * Signing in via a role you hold should not stop at a chooser, so the hint from
 * the sign-in button is spent here: it either resolves to a workspace, and the
 * caller lands on its URL, or it does not, and it travels to the chooser as
 * navigation state so that screen can say which access is missing. Either way
 * the address bar ends up on a real page, never on `/`.
 */
export function LandingRedirect() {
  const { session, loading, me } = useAccount();
  const intended = landingIntent();

  if (loading) return <Booting />;
  if (!session) return <Navigate to={PATHS.signIn} replace />;

  const view = resolveWorkspace({ me, intended });
  if (view.kind === "loading") return <Booting />;

  if (view.kind === "open") {
    return <Navigate to={workspacePath(view.workspace)} replace />;
  }

  return <Navigate to={PATHS.workspaces} replace state={{ intended }} />;
}
